import { useState } from 'react'
import { Box, Button, Divider, Link, Paper, Typography } from '@mui/material'
import { ConfirmationModal } from 'components/utilities/confirmationModal'
import logo from '../../logo.png'
import { ReferralVerificationData } from './types'

export interface ReferralAllowCardProps {
  referral: ReferralVerificationData
  trackingRef: string
  question: string
  initialChoice?: 'Yes' | 'No'
  onSubmit: (response: 'Yes' | 'No') => void | Promise<void>
}

export const ReferralAllowCard = ({
  referral,
  trackingRef,
  question,
  initialChoice,
  onSubmit,
}: ReferralAllowCardProps): JSX.Element => {
  const [selected, setSelected] = useState<'Yes' | 'No' | undefined>(
    initialChoice
  )
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const centerName = referral.center?.name || 'this resource'

  const handleChoose = (choice: 'Yes' | 'No'): void => {
    setSelected(choice)
    setShowConfirmModal(true)
  }

  const handleConfirm = (): void => {
    if (!selected) return
    onSubmit(selected)
  }

  return (
    <Box>
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems="flex-start"
        mb={2}
        gap={2}
      >
        <Box>
          <Typography variant="h6" color="primary.main" fontWeight={600}>
            Referral Verification
          </Typography>
          <Typography variant="body1" color="text.disabled">
            This request is for{' '}
            <Box component="span" color="text.primary" fontWeight={700}>
              {centerName}
            </Box>
            .
          </Typography>
        </Box>
        <Box
          component="img"
          src={logo}
          alt="resolvexLogo"
          sx={{ width: { xs: 64, sm: 96 } }}
        />
      </Box>

      <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Typography variant="caption" color="text.secondary">
          Tracking Ref: {trackingRef}
        </Typography>
        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" fontWeight="bold" textAlign="center">
          {question}
        </Typography>

        <Divider sx={{ my: 2 }} />

        {!selected && (
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={2}
          >
            <Button
              fullWidth
              variant="outlined"
              color="error"
              size="large"
              onClick={() => handleChoose('No')}
            >
              No
            </Button>
            <Button
              fullWidth
              variant="outlined"
              color="success"
              size="large"
              onClick={() => handleChoose('Yes')}
            >
              Yes
            </Button>
          </Box>
        )}

        {selected && (
          <Box display="flex" flexDirection="column" alignItems="center">
            <Button
              fullWidth
              variant="contained"
              color={selected === 'Yes' ? 'success' : 'error'}
              size="large"
              onClick={() => setShowConfirmModal(true)}
            >
              {selected}
            </Button>
            <Link
              component="button"
              variant="body2"
              underline="hover"
              sx={{ mt: 1.5 }}
              onClick={() => setSelected(undefined)}
            >
              Change response
            </Link>
          </Box>
        )}
      </Paper>

      <ConfirmationModal
        open={showConfirmModal}
        setOpen={setShowConfirmModal}
        title="Confirm your response"
        color={selected === 'No' ? 'error' : 'success'}
        message={`Are you sure you want to answer "${selected}" to this question?`}
        callback={handleConfirm}
      />
    </Box>
  )
}
